// Client-side image downscaling/compression before upload. Phone photos are
// often 5-12 MB; shrinking them to <=1920px JPEG (~0.3-1 MB) makes uploads
// fast and reliable, avoids proxy size limits, and keeps the small backend
// from running out of memory. Non-images and un-decodable files pass through
// unchanged, so nothing ever breaks because of compression.

async function loadBitmap(file) {
  if ("createImageBitmap" in window) {
    try {
      return await createImageBitmap(file);
    } catch (_) {
      /* fall through to <img> loader */
    }
  }
  return await new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

export async function compressImageFile(file, { maxPx = 1920, quality = 0.8 } = {}) {
  if (!file || !(file.type || "").startsWith("image/")) return file;
  // GIF/SVG can't be safely rasterised to a single JPEG frame — leave as-is.
  if (file.type === "image/gif" || file.type === "image/svg+xml") return file;

  try {
    const bitmap = await loadBitmap(file);
    const width = bitmap.width || bitmap.naturalWidth;
    const height = bitmap.height || bitmap.naturalHeight;
    if (!width || !height) return file;

    const scale = Math.min(1, maxPx / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    // Flatten transparency onto white so JPEG looks correct.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(bitmap, 0, 0, w, h);
    if (bitmap.close) bitmap.close();

    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    // Don't use the result if compression didn't actually help.
    if (!blob || blob.size >= file.size) return file;

    const baseName = (file.name || "image").replace(/\.[^.]+$/i, "");
    return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } catch (_) {
    return file; // any failure → upload the original untouched
  }
}
